import { UsageTable } from '@/components/api-keys/UsageTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

interface FilterState {
  userEmail: string;
  statusCode: string;
  from: string;
  to: string;
}

const EMPTY: FilterState = { userEmail: '', statusCode: '', from: '', to: '' };

function toIsoOrUndefined(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export function AdminApiKeysUsage() {
  const [draft, setDraft] = useState<FilterState>(EMPTY);
  const [filters, setFilters] = useState<FilterState>(EMPTY);

  // userEmail debounce 300ms
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((prev) => ({ ...prev, userEmail: draft.userEmail }));
    }, 300);
    return () => clearTimeout(handle);
  }, [draft.userEmail]);

  // 즉시 반영
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      statusCode: draft.statusCode,
      from: draft.from,
      to: draft.to,
    }));
  }, [draft.statusCode, draft.from, draft.to]);

  const fromIso = useMemo(() => toIsoOrUndefined(filters.from), [filters.from]);
  const toIso = useMemo(() => toIsoOrUndefined(filters.to), [filters.to]);
  const userEmail = filters.userEmail.trim() || undefined;
  const statusCode = filters.statusCode ? Number(filters.statusCode) : undefined;

  const query = useInfiniteQuery({
    queryKey: ['admin', 'api-keys', 'usage', userEmail, statusCode, fromIso, toIso],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.adminApiKeysUsage({
        userEmail,
        statusCode,
        from: fromIso,
        to: toIso,
        cursor: pageParam,
        limit: 50,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [query]);

  function reset() {
    setDraft(EMPTY);
    setFilters(EMPTY);
  }

  return (
    <PageShell
      header={
        <>
          <PageHeader
            title="API 사용량"
            count={items.length}
            description="LLM 릴레이 프록시 호출 기록. 토큰 정보는 vLLM 응답에 usage 가 포함된 경우만 표시됩니다."
            actions={
              <Button variant="secondary" onClick={() => query.refetch()}>
                새로고침
              </Button>
            }
          />
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <FilterField label="사용자 이메일">
              <input
                type="text"
                value={draft.userEmail}
                onChange={(e) => setDraft((s) => ({ ...s, userEmail: e.target.value }))}
                placeholder="user@..."
                className="h-9 w-56 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="상태 코드">
              <select
                value={draft.statusCode}
                onChange={(e) => setDraft((s) => ({ ...s, statusCode: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              >
                <option value="">전체</option>
                <option value="200">200 (OK)</option>
                <option value="401">401 (Unauthorized)</option>
                <option value="403">403 (Forbidden)</option>
                <option value="429">429 (Too Many Requests)</option>
                <option value="499">499 (Client Aborted)</option>
                <option value="502">502 (Bad Gateway)</option>
              </select>
            </FilterField>
            <FilterField label="시작">
              <input
                type="datetime-local"
                value={draft.from}
                onChange={(e) => setDraft((s) => ({ ...s, from: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <FilterField label="끝">
              <input
                type="datetime-local"
                value={draft.to}
                onChange={(e) => setDraft((s) => ({ ...s, to: e.target.value }))}
                className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
              />
            </FilterField>
            <Button variant="ghost" size="sm" onClick={reset}>
              초기화
            </Button>
          </div>
        </>
      }
    >
      {query.isError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {query.error instanceof ApiError ? query.error.message : '사용량을 불러오지 못했습니다.'}
        </div>
      ) : null}

      <UsageTable items={items} showUser={true} />

      <div ref={sentinelRef} className="h-8" />
      {query.isFetchingNextPage ? (
        <p className="mt-2 text-center text-xs text-foreground-muted">불러오는 중…</p>
      ) : !query.hasNextPage && items.length > 0 ? (
        <p className="mt-2 text-center text-xs text-foreground-muted">끝</p>
      ) : null}
    </PageShell>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-foreground-muted">{label}</span>
      {children}
    </label>
  );
}
